import { Box, Button, Text, VStack } from "@chakra-ui/react";

const DEFAULT_LIMIT = 100;

export type CustomConversionsWarningProps = {
  /** Current number of custom conversions on the ad account */
  count: number;
  /** Meta limit for custom conversions (from API meta config); fallback used if not provided */
  limit?: number;
  /** Show warning when count >= limit - threshold (default 5, i.e. when within 5 of the limit) */
  threshold?: number;
  /** Called when user clicks "Manage custom conversions" */
  onManageClick: () => void;
  /** When true, show a "Manage custom conversions" link even when below threshold (default true) */
  showManageLinkBelowThreshold?: boolean;
};

export default function CustomConversionsWarning({
  count,
  limit = DEFAULT_LIMIT,
  threshold = 5,
  onManageClick,
  showManageLinkBelowThreshold = true,
}: CustomConversionsWarningProps) {
  const displayLimit = limit ?? DEFAULT_LIMIT;
  const warningThreshold = Math.max(0, displayLimit - threshold);
  const showWarning = count >= warningThreshold;
  const showManageLink =
    showWarning || (showManageLinkBelowThreshold && count > 0);

  if (!showManageLink) return null;

  const manageButton = (
    <Button
      size="sm"
      variant={showWarning ? "outline" : "ghost"}
      alignSelf="flex-start"
      onClick={onManageClick}
    >
      Manage custom conversions
    </Button>
  );

  if (showWarning) {
    return (
      <Box
        w="full"
        p={4}
        borderRadius="xl"
        border="1px solid"
        backgroundColor="yellow.50"
        borderColor="gray.200"
      >
        <VStack spacing={3} alignItems="flex-start">
          <VStack spacing={1} alignItems="flex-start">
            <Text fontWeight="semibold">
              Near custom conversions limit ({count}/{displayLimit})
            </Text>
            <Text fontSize="sm" color="gray.500">
              Your ad account is close to Meta's limit of {displayLimit} custom
              conversions. You can free space by deleting conversions from past
              events.
            </Text>
          </VStack>
          {manageButton}
        </VStack>
      </Box>
    );
  }

  return manageButton;
}
